import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, DownloadSimple, Printer } from "@phosphor-icons/react";

export default function ProposalPreview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/proposals/${id}/preview`); setData(data); }
      catch (e) { toast.error("Failed to load preview"); }
    })();
  }, [id]);

  const exportFile = async (fmt) => {
    try {
      const token = localStorage.getItem("sakshi_token");
      const res = await fetch(`${API}/proposals/${id}/export.${fmt}`, { headers: { Authorization: `Bearer ${token}` }});
      if (!res.ok) { const j = await res.json().catch(()=>({detail:"Failed"})); throw new Error(j.detail?.message || j.detail || "Export blocked"); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${data?.meta?.client_legal_name||"proposal"}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${fmt.toUpperCase()} exported`);
    } catch (e) { toast.error(String(e.message||e)); }
  };

  if (!data) return <div className="p-8 text-sm text-slate-500">Loading preview…</div>;

  const b = data.branding;
  const meta = data.meta;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="proposal-preview">
      {/* toolbar */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => nav(`/proposals/${id}`)} data-testid="preview-back" className="inline-flex items-center gap-1 text-sm border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-100">
              <ArrowLeft size={14}/> <span className="hidden sm:inline">Back to editor</span><span className="sm:hidden">Back</span>
            </button>
            <Link to={`/proposals/${id}`} data-testid="preview-edit-full" className="inline-flex items-center gap-1 text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-3 py-1.5 transition-colors duration-200">
              Edit all fields
            </Link>
            <div className="min-w-0">
              <div className="label-micro">Live preview · {b.cover_style}</div>
              <div className="font-display font-bold text-sm truncate">{meta.client_legal_name} · {meta.proposal_type}</div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/branding" className="inline-flex items-center gap-1 text-sm border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-100" data-testid="preview-edit-branding"><span className="hidden sm:inline">Edit branding</span><span className="sm:hidden">Brand</span></Link>
            <button onClick={() => window.print()} data-testid="preview-print" className="inline-flex items-center gap-1 text-sm border border-slate-300 rounded-sm px-3 py-1.5 hover:bg-slate-100"><Printer size={14}/> <span className="hidden sm:inline">Print</span></button>
            <button onClick={() => exportFile("docx")} data-testid="preview-export-docx" className="inline-flex items-center gap-1 text-sm bg-slate-900 hover:bg-blue-700 text-white rounded-sm px-3 py-1.5 transition-colors duration-200"><DownloadSimple size={14}/>DOCX</button>
            <button onClick={() => exportFile("pdf")} data-testid="preview-export-pdf" className="inline-flex items-center gap-1 text-sm border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-sm px-3 py-1.5 transition-colors duration-200"><DownloadSimple size={14}/>PDF</button>
          </div>
        </div>
      </div>

      {/* A4 page */}
      <div className="max-w-4xl mx-auto py-6 sm:py-10 print:py-0 px-3 sm:px-6">
        <article className="bg-white shadow-lg mx-auto p-6 sm:p-10 lg:p-16 print:shadow-none print:p-12 origin-top" style={{ maxWidth: "794px", width: "100%" }} data-testid="preview-doc">
          {/* Cover */}
          <div className="flex" style={{ gap: b.cover_style === "left_bar" ? 16 : 0 }}>
            {b.cover_style === "left_bar" && <div style={{ width: 4, backgroundColor: b.accent_color }} className="self-stretch min-h-[120px]"/>}
            <div className="flex-1">
              {b.logo_data_uri && <img src={b.logo_data_uri} alt="Logo" className="h-14 object-contain mb-4"/>}
              <div className="text-xs font-bold tracking-widest uppercase" style={{ color: b.accent_color }}>{b.company_name}</div>
              <h1 className="font-display font-black mt-2 leading-tight" style={{ color: b.primary_color, fontSize: b.cover_style === "banner" ? 44 : 32 }}>
                {b.cover_title}
              </h1>
              <div className="mt-6 text-sm text-slate-800 space-y-1.5">
                <div><span className="font-semibold">Prepared for:</span> {meta.client_legal_name}</div>
                <div><span className="font-semibold">Recipient:</span> {meta.recipient_name}{meta.recipient_designation ? `, ${meta.recipient_designation}` : ""}</div>
                <div><span className="font-semibold">Proposal type:</span> <span className="capitalize">{meta.proposal_type}</span></div>
                <div><span className="font-semibold">Issue date:</span> {meta.issue_date}</div>
                <div><span className="font-semibold">Prepared by:</span> {meta.prepared_by_name}</div>
                <div><span className="font-semibold">Valid for:</span> {meta.validity_days} days</div>
                {meta.version_number > 0 && <div><span className="font-semibold">Version:</span> v{meta.version_number}</div>}
              </div>
            </div>
          </div>

          <div className="mt-12 space-y-8">
            {data.sections.map((s, i) => (
              <section key={i} data-testid={`preview-section-${i}`}>
                <h2 className="font-display font-bold text-lg pb-2 border-b" style={{ color: b.primary_color, borderColor: b.primary_color + "22" }}>{s.heading}</h2>
                {s.signature_block ? (
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <div className="text-xs italic text-slate-500">For and on behalf of</div>
                      <div className="font-semibold" style={{ color: b.primary_color }}>{b.company_name}</div>
                      <div className="mt-6 h-16 flex items-end">
                        {b.signature_data_uri ? (
                          <img src={b.signature_data_uri} alt="Signature" className="max-h-16 object-contain"/>
                        ) : (
                          <div className="w-full border-b border-slate-400"/>
                        )}
                      </div>
                      <div className="text-xs mt-2 space-y-0.5">
                        <div>Name: <b>{b.signatory_name || "_______________________"}</b></div>
                        <div>Designation: <b>{b.signatory_title || "_______________________"}</b></div>
                        {b.signatory_email && <div>Email: {b.signatory_email}</div>}
                        <div>Date: _______________________</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs italic text-slate-500">Accepted by</div>
                      <div className="font-semibold" style={{ color: b.primary_color }}>{meta.client_legal_name}</div>
                      <div className="mt-6 h-16 flex items-end"><div className="w-full border-b border-slate-400"/></div>
                      <div className="text-xs mt-2 space-y-0.5">
                        <div>Name: <b>{meta.recipient_name || "_______________________"}</b></div>
                        <div>Designation: <b>{meta.recipient_designation || "_______________________"}</b></div>
                        <div>Date: _______________________</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                {s.body && (
                  <div className="mt-3 text-sm text-slate-800 leading-relaxed whitespace-pre-line">{s.body}</div>
                )}
                {s.pricing && (
                  <div className="mt-4">
                    <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse min-w-[520px]">
                      <thead>
                        <tr style={{ backgroundColor: b.primary_color, color: "white" }}>
                          <th className="text-left px-3 py-2 font-semibold">Item</th>
                          <th className="text-left px-3 py-2 font-semibold">Mode</th>
                          <th className="text-right px-3 py-2 font-semibold">Qty</th>
                          <th className="text-right px-3 py-2 font-semibold">Unit</th>
                          <th className="text-right px-3 py-2 font-semibold">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.pricing.map((l, j) => (
                          <tr key={j} className="border-b border-slate-200">
                            <td className="px-3 py-2">{l.label} <span className="text-slate-500">({l.charge_type})</span></td>
                            <td className="px-3 py-2">{l.mode}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{l.quantity}</td>
                            <td className="px-3 py-2 text-right tabular-nums">₹{((l.unit_price||0)/100).toLocaleString("en-IN")}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold">₹{((l.total_amount||0)/100).toLocaleString("en-IN")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    {s.totals_formatted && (
                      <div className="mt-3 text-sm space-y-1">
                        <div><b>Tax treatment:</b> {s.tax_treatment?.replace(/_/g, " ")}</div>
                        <div><b>One-time total:</b> {s.totals_formatted.one_time_paise}</div>
                        <div><b>Recurring monthly:</b> {s.totals_formatted.recurring_monthly_paise}</div>
                        <div className="pt-1 border-t border-slate-200"><b>Year-one total:</b> <span className="font-bold" style={{ color: b.accent_color }}>{s.totals_formatted.year_one_total_paise}</span></div>
                      </div>
                    )}
                  </div>
                )}
                  </>
                )}
              </section>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-16 pt-4 border-t border-slate-200 flex items-center justify-between text-[10px] italic text-slate-500">
            <span>{b.footer_text}</span>
            <span>{meta.client_legal_name}</span>
          </div>
        </article>
      </div>
    </div>
  );
}
